import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Input,
  Modal,
  ModalDialog,
  Sheet,
  Stack,
  Table,
  Typography,
} from "@mui/joy";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  OrganizationIntegrationSubstrateItem,
  OrganizationIntegrationSubstrateItemCreate,
  OrganizationIntegrationSubstrateItemUpdate,
} from "../../common/types";
import {
  useCreateOrganizationIntegrationSubstrateItem,
  useDeleteOrganizationIntegrationSubstrateItem,
  useListOrganizationIntegrationSubstrateItems,
  useUpdateOrganizationIntegrationSubstrateItem,
} from "../../services/api";

interface EmailSendersManagerProps {
  open: boolean;
  organizationId: string;
  organizationIntegrationId: string;
  onClose: () => void;
}

interface SenderDraft {
  senderAddress: string;
  displayLabel: string;
}

const emptyDraft: SenderDraft = {
  senderAddress: "",
  displayLabel: "",
};

const readSenderAddress = (
  item?: OrganizationIntegrationSubstrateItem | null,
) =>
  String(
    item?.config?.sender_address ||
      item?.config?.default_sender_address ||
      item?.config?.email_address ||
      item?.config?.address ||
      "",
  );

const readSenderDraft = (
  item?: OrganizationIntegrationSubstrateItem | null,
): SenderDraft => ({
  senderAddress: readSenderAddress(item),
  displayLabel: String(item?.display_label || ""),
});

const EmailSendersManager: React.FC<EmailSendersManagerProps> = ({
  open,
  organizationId,
  organizationIntegrationId,
  onClose,
}) => {
  const listItems = useListOrganizationIntegrationSubstrateItems();
  const createItem = useCreateOrganizationIntegrationSubstrateItem();
  const updateItem = useUpdateOrganizationIntegrationSubstrateItem();
  const deleteItem = useDeleteOrganizationIntegrationSubstrateItem();
  const [items, setItems] = useState<OrganizationIntegrationSubstrateItem[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingItem, setEditingItem] =
    useState<OrganizationIntegrationSubstrateItem | null>(null);
  const [draft, setDraft] = useState<SenderDraft>(emptyDraft);
  const [deleteTarget, setDeleteTarget] =
    useState<OrganizationIntegrationSubstrateItem | null>(null);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setItems(await listItems(organizationId, organizationIntegrationId));
    } catch (requestError: any) {
      setError(
        requestError?.message || "Failed to load email sender substrate items.",
      );
    } finally {
      setLoading(false);
    }
  }, [listItems, organizationId, organizationIntegrationId]);

  useEffect(() => {
    if (!open) return;
    loadItems();
  }, [loadItems, open]);

  const openAdd = () => {
    setEditingItem(null);
    setDraft(emptyDraft);
    setEditorOpen(true);
  };

  const openEdit = (item: OrganizationIntegrationSubstrateItem) => {
    setEditingItem(item);
    setDraft(readSenderDraft(item));
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingItem(null);
    setDraft(emptyDraft);
  };

  const savingDisabled = useMemo(
    () => saving || draft.senderAddress.trim() === "",
    [draft.senderAddress, saving],
  );

  const handleSave = async () => {
    const basePayload = {
      display_label: draft.displayLabel.trim() || null,
      config: {
        sender_address: draft.senderAddress.trim(),
      },
    };

    try {
      setSaving(true);
      if (editingItem) {
        const payload: OrganizationIntegrationSubstrateItemUpdate = basePayload;
        await updateItem(
          organizationId,
          organizationIntegrationId,
          editingItem.id,
          payload,
        );
      } else {
        const payload: OrganizationIntegrationSubstrateItemCreate = basePayload;
        await createItem(organizationId, organizationIntegrationId, payload);
      }
      await loadItems();
      closeEditor();
    } catch (saveError: any) {
      setError(saveError?.message || "Failed to save sender.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setSaving(true);
      await deleteItem(
        organizationId,
        organizationIntegrationId,
        deleteTarget.id,
      );
      await loadItems();
      setDeleteTarget(null);
    } catch (deleteError: any) {
      setError(deleteError?.message || "Failed to delete sender.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <ModalDialog sx={{ width: "min(960px, calc(100vw - 32px))" }}>
          <DialogTitle>Manage email senders</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              {error && (
                <Alert color="danger" variant="soft">
                  {error}
                </Alert>
              )}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                spacing={1}
              >
                <Typography level="body-sm" color="neutral">
                  Add, rename, and remove sender identities that workspaces can
                  use for templates and outbound email.
                </Typography>
                <Button
                  size="sm"
                  variant="solid"
                  startDecorator={<AddIcon />}
                  onClick={openAdd}
                >
                  Add another sender
                </Button>
              </Stack>

              <Sheet
                variant="outlined"
                sx={{ borderRadius: "8px", overflowX: "auto" }}
              >
                <Table hoverRow sx={{ minWidth: 720 }}>
                  <thead>
                    <tr>
                      <th style={{ width: "40%" }}>Sender address</th>
                      <th style={{ width: "25%" }}>Label</th>
                      <th style={{ width: "20%" }}>Last modified</th>
                      <th style={{ width: "15%", textAlign: "right" }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.id}>
                        <td>{readSenderAddress(item) || "Unknown sender"}</td>
                        <td>{item.display_label || "—"}</td>
                        <td>{item.updated_at || "—"}</td>
                        <td style={{ textAlign: "right" }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                          >
                            <Button
                              size="sm"
                              variant="outlined"
                              color="neutral"
                              startDecorator={<EditIcon />}
                              onClick={() => openEdit(item)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outlined"
                              color="danger"
                              startDecorator={<DeleteIcon />}
                              onClick={() => setDeleteTarget(item)}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </td>
                      </tr>
                    ))}
                    {!loading && items.length === 0 && (
                      <tr>
                        <td colSpan={4}>
                          <Typography level="body-sm" color="neutral">
                            No email senders configured yet.
                          </Typography>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Sheet>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="plain" color="neutral" onClick={onClose}>
              Done
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={editorOpen} onClose={closeEditor}>
        <ModalDialog sx={{ width: "min(520px, calc(100vw - 32px))" }}>
          <DialogTitle>
            {editingItem ? "Edit sender" : "Add sender"}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1.5}>
              <Input
                value={draft.senderAddress}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    senderAddress: event.target.value,
                  }))
                }
                placeholder="alerts@example.com"
              />
              <Input
                value={draft.displayLabel}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    displayLabel: event.target.value,
                  }))
                }
                placeholder="Optional label"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleSave}
              loading={saving}
              disabled={savingDisabled}
            >
              Save
            </Button>
            <Button variant="plain" color="neutral" onClick={closeEditor}>
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <ModalDialog sx={{ width: "min(480px, calc(100vw - 32px))" }}>
          <DialogTitle>Delete sender</DialogTitle>
          <DialogContent>
            <Typography level="body-sm">
              Remove{" "}
              {deleteTarget?.display_label ||
                readSenderAddress(deleteTarget) ||
                "this sender"}
              ?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button color="danger" onClick={handleDelete} loading={saving}>
              Delete
            </Button>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </>
  );
};

export default EmailSendersManager;
